/**
 * Radio Network Panel
 *
 * Which LoRa mesh the radio is on: pick a network profile (Sacramento
 * MeshCore by default, other regions, Meshtastic, or custom), see what the
 * radio itself REPORTS, and retune it. Transmit is blocked while the radio
 * sits outside the chosen region's legal band.
 */

import { useEffect, useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { useRadioSession } from '@/hooks/useRadioSession'
import {
  LORA_BANDWIDTHS_KHZ,
  REGIONS,
  describeProfile,
  validateProfile,
  type MeshCoreProfile,
} from '@/lib/lora/profiles'
import { deriveRoomChannel, type RoomChannel } from '@/lib/meshcore'

interface RadioNetworkPanelProps {
  /** When given, offers to share this room with the stock MeshCore app. */
  roomId?: string
  roomSecret?: string
}

export function RadioNetworkPanel({ roomId, roomSecret }: RadioNetworkPanelProps) {
  const {
    radio,
    profiles,
    selected,
    radioMatchesSelected,
    applying,
    applyError,
    selectProfile,
    addCustomProfile,
    applySelected,
    scan,
    scanProgress,
    scanResults,
    scanListenSeconds,
  } = useRadioSession()
  const regionMeshCoreCount = profiles.filter((p) => p.firmware === 'meshcore' && p.region === radio.region).length
  const [showCustom, setShowCustom] = useState(false)

  const reported = radio.selfInfo?.radio
  const meshcore = profiles.filter((p) => p.firmware === 'meshcore')
  const meshtastic = profiles.filter((p) => p.firmware === 'meshtastic')

  return (
    <div className="space-y-3">
      {/* Network picker */}
      <div>
        <label htmlFor="lora-network" className="text-xs text-white/50 mb-1 block">
          Network
        </label>
        <select
          id="lora-network"
          value={selected.id}
          onChange={(e) => selectProfile(e.target.value)}
          className="w-full min-h-[48px] px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white"
        >
          <optgroup label="MeshCore">
            {meshcore.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Meshtastic">
            {meshtastic.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </optgroup>
        </select>
        <p className="mt-1 text-xs text-white/70 font-mono">{describeProfile(selected)}</p>
        <p className="mt-0.5 text-[10px] text-white/40 leading-snug">
          {REGIONS[selected.region]?.displayName} · source: {selected.source} (checked {selected.checked})
        </p>
      </div>

      {/* What the radio reports */}
      {reported && (
        <div
          className={clsx(
            'p-3 rounded-lg border text-xs',
            radio.bandProblem
              ? 'bg-red-500/10 border-red-500/40'
              : radioMatchesSelected
              ? 'bg-green-500/10 border-green-500/30'
              : 'bg-yellow-500/10 border-yellow-500/30'
          )}
        >
          <div className="text-white/50">Your radio reports</div>
          <div className="font-mono text-white/90">
            {reported.freqMHz} MHz / {reported.bwKHz} kHz / SF{reported.sf} / CR{reported.cr}
          </div>
          <div
            className={clsx(
              'mt-1 font-medium',
              radio.bandProblem ? 'text-red-400' : radioMatchesSelected ? 'text-green-400' : 'text-yellow-400'
            )}
          >
            {radio.bandProblem
              ? radio.bandProblem
              : radioMatchesSelected
              ? '✓ On this network'
              : selected.firmware === 'meshtastic'
              ? 'This is a Meshtastic network. A MeshCore radio cannot hear it.'
              : 'On a different network than the one chosen above.'}
          </div>
        </div>
      )}

      {/* Retune */}
      {reported && selected.firmware === 'meshcore' && !radioMatchesSelected && (
        <div>
          <button
            onClick={() => void applySelected()}
            disabled={applying}
            className={clsx(
              'w-full min-h-[48px] px-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all',
              applying
                ? 'bg-yellow-500/20 text-yellow-400 cursor-wait'
                : 'bg-purple-500 text-white hover:bg-purple-400 shadow-hard-sm'
            )}
          >
            {applying ? 'Tuning radio…' : 'Tune radio to this network'}
          </button>
          <p className="mt-1 text-[10px] text-white/40">
            Changes which mesh your radio hears and stays on the radio after it restarts. Takes about 10 seconds;
            the result shown is read back from the radio.
          </p>
        </div>
      )}
      {applyError && (
        <p className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400">{applyError}</p>
      )}

      {/* Find a mesh */}
      {reported && (
        <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
          {scanProgress ? (
            <p className="text-xs text-purple-300">
              Listening on {scanProgress.label} ({scanProgress.index + 1} of {scanProgress.total})…
            </p>
          ) : (
            <>
              <button
                onClick={() => void scan()}
                disabled={applying || radio.status !== 'ready'}
                className="w-full min-h-[44px] rounded-lg text-sm font-bold bg-white/10 text-white/80 hover:bg-white/20 disabled:opacity-40"
              >
                Find a mesh near me
              </button>
              <p className="text-[10px] text-white/40">
                Tries the {regionMeshCoreCount} MeshCore networks for {REGIONS[radio.region]?.displayName ?? radio.region},
                about {scanListenSeconds} s each, sending one short &quot;who&apos;s there?&quot;. Your radio is off its
                current network meanwhile and goes back to it afterwards.
              </p>
            </>
          )}
          {scanResults && (
            <ul className="space-y-1">
              {scanResults.map((r, i) => (
                <li key={r.profileId} className="flex items-center gap-2 text-xs">
                  <span className={clsx('flex-1', i === 0 && r.framesHeard + r.discoverReplies > 0 ? 'text-green-400' : 'text-white/70')}>
                    {r.label}
                    <span className="block text-[10px] text-white/40">
                      {r.error
                        ? `could not tune: ${r.error}`
                        : r.framesHeard + r.discoverReplies + r.adverts === 0
                        ? 'heard nothing'
                        : `${r.discoverReplies} answered · ${r.framesHeard} frames heard${r.bestRssi !== null ? ` · best ${r.bestRssi} dBm` : ''}`}
                    </span>
                  </span>
                  {!r.error && r.framesHeard + r.discoverReplies > 0 && r.profileId !== selected.id && (
                    <button
                      onClick={() => selectProfile(r.profileId)}
                      className="min-h-[36px] px-2 rounded bg-purple-500/20 border border-purple-500/40 text-purple-200 font-bold"
                    >
                      Use this
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Air stats */}
      {radio.status === 'ready' && (
        <p className="text-[11px] text-white/50">
          On air this session: <span className="text-white/80">{radio.sent} sent</span>
          {radio.unconfirmed > 0 && <span className="text-yellow-400"> · {radio.unconfirmed} not confirmed</span>}
          {radio.queued > 0 && <span> · {radio.queued} waiting (one every 5 s to share the channel)</span>}
        </p>
      )}

      {/* Custom profile */}
      <button
        onClick={() => setShowCustom((v) => !v)}
        className="text-xs text-white/50 underline underline-offset-2 hover:text-white/80"
      >
        {showCustom ? 'Hide custom settings' : 'Enter custom radio settings'}
      </button>
      {showCustom && <CustomProfileForm onSave={(p) => (addCustomProfile(p), setShowCustom(false))} />}

      {roomId && roomSecret && <ShareWithMeshCoreApp roomId={roomId} roomSecret={roomSecret} />}
    </div>
  )
}

function CustomProfileForm({ onSave }: { onSave: (p: MeshCoreProfile) => void }) {
  const [label, setLabel] = useState('My mesh')
  const [region, setRegion] = useState('us_915')
  const [freq, setFreq] = useState('910.525')
  const [bw, setBw] = useState(62.5)
  const [sf, setSf] = useState(7)
  const [cr, setCr] = useState(5)

  const draft: MeshCoreProfile = useMemo(
    () => ({
      id: `custom-${label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'mesh'}`,
      label: label.trim() || 'My mesh',
      firmware: 'meshcore',
      region,
      freqMHz: Number(freq),
      bwKHz: bw,
      sf,
      cr,
      source: 'entered by you',
      checked: new Date().toISOString().slice(0, 10),
      origin: 'custom',
    }),
    [label, region, freq, bw, sf, cr]
  )
  const problems = validateProfile(draft)
  const field = 'w-full min-h-[44px] px-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white'

  return (
    <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
      <p className="text-[10px] text-white/40">MeshCore radio settings. Must match your local mesh exactly.</p>
      <label className="block text-xs text-white/50">
        Name
        <input className={field} value={label} onChange={(e) => setLabel(e.target.value)} />
      </label>
      <label className="block text-xs text-white/50">
        Region (sets the legal band)
        <select className={field} value={region} onChange={(e) => setRegion(e.target.value)}>
          {Object.values(REGIONS).map((r) => (
            <option key={r.code} value={r.code}>
              {r.displayName}
            </option>
          ))}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-white/50">
          Frequency (MHz)
          <input className={field} inputMode="decimal" value={freq} onChange={(e) => setFreq(e.target.value)} />
        </label>
        <label className="block text-xs text-white/50">
          Bandwidth (kHz)
          <select className={field} value={bw} onChange={(e) => setBw(Number(e.target.value))}>
            {LORA_BANDWIDTHS_KHZ.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-white/50">
          Spreading factor
          <select className={field} value={sf} onChange={(e) => setSf(Number(e.target.value))}>
            {[5, 6, 7, 8, 9, 10, 11, 12].map((v) => (
              <option key={v} value={v}>
                SF{v}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-white/50">
          Coding rate
          <select className={field} value={cr} onChange={(e) => setCr(Number(e.target.value))}>
            {[5, 6, 7, 8].map((v) => (
              <option key={v} value={v}>
                4/{v}
              </option>
            ))}
          </select>
        </label>
      </div>
      {problems.length > 0 && (
        <ul className="text-[11px] text-red-400 space-y-0.5">
          {problems.map((p, i) => (
            <li key={i}>• {p.detail}</li>
          ))}
        </ul>
      )}
      <button
        disabled={problems.length > 0}
        onClick={() => onSave(draft)}
        className="w-full min-h-[44px] rounded-lg text-sm font-bold bg-primary/20 border border-primary text-primary disabled:opacity-40"
      >
        Save and select
      </button>
    </div>
  )
}

function ShareWithMeshCoreApp({ roomId, roomSecret }: { roomId: string; roomSecret: string }) {
  const [open, setOpen] = useState(false)
  const [channel, setChannel] = useState<RoomChannel | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!open) return
    let live = true
    void deriveRoomChannel(roomSecret, roomId).then((c) => live && setChannel(c))
    return () => {
      live = false
    }
  }, [open, roomId, roomSecret])

  const copy = async () => {
    if (!channel) return
    try {
      await navigator.clipboard.writeText(channel.secretHex)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard blocked; the key is on screen to copy by hand
    }
  }

  return (
    <div className="pt-2 border-t border-white/10">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-white/50 underline underline-offset-2 hover:text-white/80"
      >
        {open ? 'Hide' : 'Use this room in the MeshCore app'}
      </button>
      {open && channel && (
        <div className="mt-2 p-3 rounded-lg bg-white/5 border border-white/10 space-y-2 text-xs">
          <p className="text-white/60">
            Anyone without CrowdTalkie can still text this room over LoRa: in the MeshCore app, add a private
            channel with this name and secret key.
          </p>
          <div>
            <span className="text-white/40">Channel name</span>
            <div className="font-mono text-white/90">{channel.name}</div>
          </div>
          <div>
            <span className="text-white/40">Secret key (hex)</span>
            <div className="font-mono text-white/90 break-all">{channel.secretHex}</div>
          </div>
          <button
            onClick={() => void copy()}
            className="min-h-[44px] px-3 rounded-lg bg-white/10 text-white/80 hover:bg-white/20"
          >
            {copied ? 'Copied' : 'Copy key'}
          </button>
          <p className="text-[10px] text-yellow-400/80">
            This key reads and writes the room's radio text. Share it only in person, never in a public post.
          </p>
        </div>
      )}
    </div>
  )
}
